<template>
  <div class="ct-select" :class="{ 'ct-select--open': open, 'ct-select--disabled': disabled }">
    <button
      ref="trigger"
      type="button"
      class="ct-select__trigger"
      :disabled="disabled"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
      @keydown="onTriggerKey"
    >
      <span class="ct-select__value" :class="{ 'ct-select__value--placeholder': !selected }">
        {{ selected ? selected.label : placeholder }}
      </span>
      <svg class="ct-select__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>

    <!-- Fixed, not absolute: the modal body scrolls, and an absolute menu would be clipped by it. -->
    <ul
      v-if="open"
      ref="menu"
      class="ct-select__menu"
      :style="menuStyle"
      role="listbox"
      @mousedown.prevent
    >
      <li
        v-for="(option, index) in options"
        :key="String(option.value)"
        class="ct-select__option"
        :class="{
          'ct-select__option--active': index === highlighted,
          'ct-select__option--selected': isSelected(option),
        }"
        role="option"
        :aria-selected="isSelected(option) ? 'true' : 'false'"
        @mouseenter="highlighted = index"
        @click="choose(option)"
      >
        <span>{{ option.label }}</span>
        <svg v-if="isSelected(option)" width="14" height="14" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * The panel's own dropdown, for places that style their fields by hand.
 *
 * A native <select> draws its open list with the operating system: on a dark modal that list
 * arrives white with a blue highlight and ignores the theme entirely. This one is plain markup,
 * so it follows the same tokens as the inputs beside it.
 */
export default {
  name: "CtSelect",
  props: {
    value: { type: [String, Number], default: "" },
    /** [{ value, label }] — value is what v-model carries. */
    options: { type: Array, required: true },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
  },
  data: () => ({
    open: false,
    highlighted: -1,
    menuStyle: {},
  }),
  computed: {
    selected() {
      return this.options.find((option) => String(option.value) === String(this.value)) || null;
    },
  },
  beforeDestroy() {
    this.unbind();
  },
  methods: {
    isSelected(option) {
      return this.selected !== null && String(option.value) === String(this.selected.value);
    },
    toggle() {
      if (this.disabled) return;
      this.open ? this.close() : this.openMenu();
    },
    openMenu() {
      this.open = true;
      this.highlighted = this.options.findIndex((option) => this.isSelected(option));
      this.$nextTick(() => {
        this.detachMenu();
        this.makeRoomBelow();
        this.position();
        this.scrollToHighlighted();
      });
      document.addEventListener("mousedown", this.onDocumentDown, true);
      window.addEventListener("resize", this.follow, true);
      // A fixed menu does not move with a scrolling dialog body, so follow the trigger instead.
      window.addEventListener("scroll", this.follow, true);
    },
    close() {
      if (!this.open) return;
      this.open = false;
      this.unbind();
    },
    unbind() {
      document.removeEventListener("mousedown", this.onDocumentDown, true);
      window.removeEventListener("resize", this.follow, true);
      window.removeEventListener("scroll", this.follow, true);
    },
    /** Keep the menu under its trigger while something scrolls; give up once it leaves the screen. */
    follow() {
      if (!this.open) return;
      const trigger = this.$refs.trigger.getBoundingClientRect();
      if (trigger.bottom < 0 || trigger.top > window.innerHeight) {
        this.close();
        return;
      }
      this.position();
    },
    onDocumentDown(event) {
      // The menu lives at the app root once open, so it is no longer inside this.$el.
      const menu = this.$refs.menu;
      const inside = this.$el.contains(event.target) || (menu && menu.contains(event.target));
      if (!inside) this.close();
    },
    /**
     * Moves the open menu up to the app root.
     *
     * <p>Inside a dialog the menu sits in a body that scrolls and an overlay that blurs, and an
     * element with a backdrop-filter becomes the containing block for whatever is fixed inside it.
     * At the root the menu answers to the viewport alone. Vue removes the element through its
     * current parent, so moving it does not upset the later teardown.
     */
    detachMenu() {
      const menu = this.$refs.menu;
      const host = document.querySelector(".v-application") || document.body;
      if (menu && menu.parentNode !== host) host.appendChild(menu);
    },

    /** The nearest ancestor that actually scrolls — the dialog body, usually. */
    scrollableAncestor() {
      let el = this.$el.parentElement;
      while (el && el !== document.body) {
        const overflow = window.getComputedStyle(el).overflowY;
        if ((overflow === "auto" || overflow === "scroll") && el.scrollHeight > el.clientHeight) {
          return el;
        }
        el = el.parentElement;
      }
      return null;
    },

    /**
     * On a short window the field can sit close to the bottom, leaving room for two rows. Scroll
     * the dialog up by what is missing so the list still opens downwards at a usable height.
     */
    makeRoomBelow() {
      const wanted = 240;
      const trigger = this.$refs.trigger.getBoundingClientRect();
      const missing = wanted - (window.innerHeight - trigger.bottom - 14);
      if (missing <= 0) return;
      const scroller = this.scrollableAncestor();
      if (!scroller) return;
      const room = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
      if (room > 0) scroller.scrollTop += Math.min(missing, room);
    },

    /**
     * Always under the trigger, never over it: the list opening upwards hides the value you are
     * changing. When the space below is short the menu gets shorter and scrolls inside itself.
     */
    position() {
      const trigger = this.$refs.trigger.getBoundingClientRect();
      const gap = 6;
      const below = window.innerHeight - trigger.bottom - gap - 8;

      this.menuStyle = {
        position: "fixed",
        left: trigger.left + "px",
        width: trigger.width + "px",
        top: trigger.bottom + gap + "px",
        maxHeight: Math.max(96, Math.min(260, below)) + "px",
      };
    },
    choose(option) {
      this.$emit("input", option.value);
      this.$emit("change", option.value);
      this.close();
      this.$refs.trigger.focus();
    },
    onTriggerKey(event) {
      const keys = ["ArrowDown", "ArrowUp", "Enter", " ", "Escape", "Home", "End", "Tab"];
      if (!keys.includes(event.key)) return;

      if (event.key === "Tab") {
        this.close();
        return;
      }
      if (!this.open) {
        if (event.key !== "Escape") {
          event.preventDefault();
          this.openMenu();
        }
        return;
      }
      event.preventDefault();
      if (event.key === "Escape") this.close();
      else if (event.key === "Enter" || event.key === " ") this.chooseHighlighted();
      else this.move(event.key);
    },
    chooseHighlighted() {
      const option = this.options[this.highlighted];
      if (option) this.choose(option);
      else this.close();
    },
    move(key) {
      const last = this.options.length - 1;
      if (key === "Home") this.highlighted = 0;
      else if (key === "End") this.highlighted = last;
      else if (key === "ArrowDown") this.highlighted = this.highlighted >= last ? 0 : this.highlighted + 1;
      else if (key === "ArrowUp") this.highlighted = this.highlighted <= 0 ? last : this.highlighted - 1;
      this.scrollToHighlighted();
    },
    /**
     * Scrolls the menu itself rather than calling scrollIntoView: the menu is fixed but still a
     * child of the dialog body, and scrollIntoView scrolls that body, sliding the trigger up under
     * the menu.
     */
    scrollToHighlighted() {
      const menu = this.$refs.menu;
      if (!menu || this.highlighted < 0) return;
      const option = menu.children[this.highlighted];
      if (!option) return;
      const top = option.offsetTop;
      const bottom = top + option.offsetHeight;
      if (top < menu.scrollTop) menu.scrollTop = top;
      else if (bottom > menu.scrollTop + menu.clientHeight) menu.scrollTop = bottom - menu.clientHeight;
    },
  },
};
</script>

<style lang="scss" scoped>
.ct-select { position: relative; }

.ct-select__trigger {
  width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 9px 12px; border: 1px solid #e0e3eb; border-radius: 8px;
  background: #fff; color: #374151; font-size: 13px; text-align: left; cursor: pointer;
  transition: border-color .2s, box-shadow .2s;
}
.ct-select__trigger:focus { outline: none; border-color: #00274c; border-width: 2px; padding: 8px 11px; }
.ct-select--open .ct-select__trigger { border-color: #00274c; }
.ct-select--disabled .ct-select__trigger { opacity: .55; cursor: not-allowed; }

.ct-select__value--placeholder { color: #9ca3af; }
.ct-select__chevron { flex-shrink: 0; color: #9ca3af; transition: transform .2s ease; }
.ct-select--open .ct-select__chevron { transform: rotate(180deg); }

.ct-select__menu {
  z-index: 3000; margin: 0; padding: 4px; list-style: none; overflow-y: auto;
  background: #fff; border: 1px solid #e0e3eb; border-radius: 10px;
  box-shadow: 0 12px 28px rgba(16, 24, 40, .14);
}
.ct-select__option {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 8px 10px; border-radius: 6px; font-size: 13px; color: #374151; cursor: pointer;
}
.ct-select__option--active { background: #f3f4f6; }
.ct-select__option--selected { color: #00274c; font-weight: 600; }

/* ── Dark Mode ── */
.theme--dark .ct-select__trigger { background: #131722; border-color: #2a2e39; color: #d1d4dc; }
.theme--dark .ct-select__trigger:focus { border-color: #ffd451; }
.theme--dark .ct-select--open .ct-select__trigger { border-color: #ffd451; }
.theme--dark .ct-select__value--placeholder { color: #787b86; }
.theme--dark .ct-select__chevron { color: #787b86; }
.theme--dark .ct-select__menu { background: #1e222d; border-color: #2a2e39; box-shadow: 0 14px 34px rgba(0, 0, 0, .5); }
.theme--dark .ct-select__option { color: #d1d4dc; }
.theme--dark .ct-select__option--active { background: #252a36; }
.theme--dark .ct-select__option--selected { color: #ffd451; }
</style>
